import { useState } from 'react';

import { captureScreenshot } from '@/features/report-problem/lib/captureScreenshot';
import { ReportProblemModal } from '@/features/report-problem/ui/ReportProblemModal';

interface IOpenOptions {
  /** Задержка перед снятием (ждём закрытия Drawer / схлопывания рельсы) */
  delayMs?: number;
  /** Снимать ли экран автоматически. false — пользователь приложит свой файл */
  capture?: boolean;
}

interface IUseReportProblem {
  /**
   * Снять скриншот и открыть модалку
   * @param options - задержка и режим снятия скрина
   */
  openReportProblem: (options?: IOpenOptions) => Promise<void>;
  /** Модалка обратной связи — рендерится потребителем хука */
  reportProblemModal: React.ReactNode;
}

/**
 * Флоу «Сообщить о проблеме»: снимок текущего экрана → модалка с формой.
 * Скрин снимается до открытия модалки, поэтому сама модалка в кадр не попадает.
 */
export const useReportProblem = (): IUseReportProblem => {
  const [screenshot, setScreenshot] = useState<HTMLCanvasElement | null>(null);
  const [isCaptureSkipped, setIsCaptureSkipped] = useState(false);

  const [isOpen, setIsOpen] = useState(false);

  /**
   * Снять скриншот и открыть модалку
   * @param options - задержка и режим снятия скрина
   * @param options.delayMs - задержка перед снятием
   * @param options.capture - снимать ли экран автоматически
   */
  const openReportProblem = async ({
    delayMs = 0,
    capture = true
  }: IOpenOptions = {}) => {
    setIsCaptureSkipped(!capture);

    // Со страницы поддержки снимать нечего: её собственный скрин проблему не покажет
    if (!capture) {
      setScreenshot(null);
      setIsOpen(true);

      return;
    }

    if (delayMs > 0) {
      await new Promise(resolve => {
        setTimeout(resolve, delayMs);
      });
    }

    setScreenshot(await captureScreenshot());
    setIsOpen(true);
  };

  /** Закрытие модалки: canvas отпускаем сразу — на retina он держит мегабайты */
  const closeReportProblem = () => {
    setIsOpen(false);
    setScreenshot(null);
    setIsCaptureSkipped(false);
  };

  const reportProblemModal = (
    <ReportProblemModal
      open={isOpen}
      onClose={closeReportProblem}
      screenshot={screenshot}
      isCaptureSkipped={isCaptureSkipped}
    />
  );

  return { openReportProblem, reportProblemModal };
};
